import { ref } from 'vue';
import { defineStore } from 'pinia';
import TMessageDialog from '@/components/dialog/TMessageDialog';
import type { TOrientacaoTela } from '@/shared/utils/WindowUtils';

type TMenuAvatar = {
  icone: string;
  titulo: string;
  visivel: boolean;
  callback: () => void;
};

type TLoading = {
  tamanho?: number;
  visivel: boolean;
  mensagem: string;
  percentual?: number;
};

type TNotification = {
  error: any;
  titulo: string;
  mensagem: string;
  timeoutMs?: number;
  tipo: 'success' | 'warning' | 'error' | 'info';
};

type TNotificationSnackbar = {
  exibir: boolean;
  mensagem: string;
  timeoutMs: number;
};

type TDialog = {
  exibir: boolean;
  titulo?: string;
  mensagem: any;
  error: any;
  tipo: 'info' | 'success' | 'alert' | 'error' | 'question';
  okLabel?: string;
  okCallback?: () => void;
  cancelLabel?: string;
  cancelCallback?: () => void;
  elementFocus?: HTMLElement | null;
};

type TConfigScreen = {
  /**Define se o dispositivo atual consegue aceitar/lidar com diferentes orientações */
  fullscreen: boolean;
  orientacao: TOrientacaoTela;
  suportaMudancaOrientacao: boolean;
};

export const useLayoutStore = defineStore('[LAYOUT]', () => {
  const miniVariant = ref<boolean>(false);
  const menusAvatar = ref<TMenuAvatar[]>([]);

  const drawerSidebarOpen = ref<boolean>(true);

  const loading = ref<TLoading>({ visivel: false, mensagem: '', percentual: 0, tamanho: 100 });
  const configScreen = ref<TConfigScreen>({ orientacao: '-', fullscreen: false, suportaMudancaOrientacao: false });

  const notification = ref<TNotification>({
    titulo: '',
    error: null,
    mensagem: '',
    timeoutMs: 5000,
    tipo: 'success',
  });

  const notificationSnackabar = ref<TNotificationSnackbar>({
    exibir: false,
    mensagem: '',
    timeoutMs: 0,
  });

  const messageDialog = ref<TDialog>({
    exibir: false,
    tipo: 'info',
    titulo: '',
    error: null,
    mensagem: '',
    okCallback: undefined,
    cancelCallback: undefined,
  });

  //-------------------
  // Manipulação do Loading
  //-------------------
  function getLoading() {
    return loading.value;
  }

  function setLoading(pParams: Partial<TLoading>) {
    loading.value.visivel = pParams.visivel ?? false;
    loading.value.mensagem = pParams.mensagem ?? '';
    loading.value.percentual = pParams.percentual;
  }

  //-------------------
  // Manipulação do Snackbar
  //-------------------

  function getNotification() {
    return notification.value;
  }

  function setNotification(pParametros: Partial<TNotification>) {
    if (pParametros.tipo === 'error' && !pParametros.mensagem) {
      if (pParametros.error instanceof Error) {
        // Error Generico

        pParametros.mensagem = String(pParametros.error.message ? pParametros.error.message : pParametros.error);
      } else if (pParametros.error.tipo && pParametros.error.mensagem && pParametros.error.erro) {
        // API Agendamento

        pParametros.mensagem = pParametros.error.erro;
        pParametros.titulo = pParametros.error.mensagem;
      } else {
        // TekServer
        pParametros.mensagem = pParametros.error;
      }
    }

    notification.value.titulo = pParametros.titulo ?? '';
    notification.value.mensagem = String(pParametros.mensagem);
    notification.value.tipo = pParametros.tipo ? pParametros.tipo : 'success';
    notification.value.timeoutMs = pParametros.timeoutMs ? pParametros.timeoutMs : 5000;
  }

  //-------------------
  // Manipulação do Dialog
  //-------------------
  function getMessageDialog() {
    return messageDialog.value;
  }

  function setMessageDialog(pParametros: Partial<TDialog>) {
    let instancia = null;

    // Tratando a mensagem dinamicamente caso nao seja inforamda
    if (pParametros.tipo === 'error' && !pParametros.mensagem) {
      if (pParametros.error instanceof Error) {
        // Error Generico

        pParametros.mensagem = String(pParametros.error.message ? pParametros.error.message : pParametros.error);
      } else if (pParametros.error.tipo || pParametros.error.mensagem || pParametros.error.erro) {
        // API

        pParametros.mensagem = pParametros.error.erro;
        pParametros.titulo = pParametros.error.mensagem;

        if (pParametros.titulo && !pParametros.mensagem) {
          pParametros.mensagem = pParametros.titulo;
          pParametros.titulo = 'Erro';
        }
      } else {
        // TekServer
        pParametros.mensagem = pParametros.error;
      }
    }

    // Definindo a instancia do dialog conforme o tipo
    switch (pParametros.tipo) {
      case 'question':
        instancia = TMessageDialog.question();
        break;
      case 'error':
        instancia = TMessageDialog.error();
        break;
      case 'alert':
        instancia = TMessageDialog.alert();
        break;
      case 'success':
        instancia = TMessageDialog.success();
        break;
      default:
        instancia = TMessageDialog.info();
        break;
    }

    instancia.title(pParametros.titulo).message(pParametros.mensagem);

    if (pParametros.okCallback) {
      instancia.okCallback(() => {
        pParametros?.okCallback?.();
      });
    }

    if (pParametros.okLabel) {
      instancia.okLabel(pParametros.okLabel);
    }

    if (pParametros.cancelCallback) {
      instancia.cancelCallback(() => {
        pParametros?.cancelCallback?.();
      });
    }

    if (pParametros.cancelLabel) {
      instancia.cancelLabel(pParametros.cancelLabel);
    }

    // Log centrtalizado
    switch (pParametros.tipo) {
      case 'info':
        console.info(pParametros.mensagem);
        break;
      case 'success':
        console.log(pParametros.mensagem);
        break;
      case 'alert':
        console.warn(pParametros.mensagem);
        break;
      case 'error':
        console.error(pParametros.mensagem);
        break;
    }

    instancia.show();
  }

  //-------------------
  // Manipulação do Snackbar
  //-------------------

  function getNotificationSnackbar() {
    return notificationSnackabar.value;
  }

  function setNotificationSnackbar(pParams: Partial<TNotificationSnackbar>) {
    if (pParams.mensagem) notificationSnackabar.value.mensagem = pParams.mensagem;
    if (pParams.timeoutMs) notificationSnackabar.value.timeoutMs = pParams.timeoutMs;
  }

  return {
    getLoading,
    setLoading,

    getNotificationSnackbar,
    setNotificationSnackbar,

    getNotification,
    setNotification,

    getMessageDialog,
    setMessageDialog,

    configScreen,
    miniVariant,
    menusAvatar,
    drawerSidebarOpen,
  };
});
